/**
 * The search input bar for the sitewide Header.
 * User may either press enter to search for the current term,
 * or click an autocomplete result to be taken to the search page.
 * Once a search term is longer than 2 characters, will issue autocomplete
 * requests, and display the results in a dropdown.
 * Also contains a search upsell upon hovering over the down caret on the right side.
 */

import React, {
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
  useCallback,
  RefObject,
} from 'react';
import styled from 'astroturf/react';
import throttle from 'lodash/throttle';

import { Icon } from 'js/design_system/Icon';
import $image_url from 'js/utils/image_url';
import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
import routes from 'sitewide-js/core_pre_webpack/routes';

import AutoCompleteResults from './AutoCompleteResults';
import { SearchResult } from './AutoCompleteResultRow';
import SearchUpsell from './SearchUpsell';

const SEARCH_INPUT_HEIGHT = 40;
const SEARCH_TEXTAREA_MAX_HEIGHT = 120;
const SEARCH_TEXTAREA_MAX_HEIGHT_MOBILE = 180;
const SEARCH_OVERLAY_GAP = 8;

const SearchTextarea = styled.textarea`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  width: 100%;
  min-height: ${SEARCH_INPUT_HEIGHT}px;
  padding: 10px 40px 10px 34px;
  color: $ds-black;
  background-color: $ds-white;
  border-radius: 4px;
  border: 1px solid #b1b1ba;
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.04);
  resize: none;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: #b1b1ba transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #b1b1ba;
    border-radius: 999px;
  }

  @media (max-width: 767px) {
    padding: 8px 20px 8px 28px;
  }

  &:focus {
    outline: 0;
  }

  &::placeholder {
    color: $ds-gray-dark;
    opacity: 1;
  }

  &::-ms-input-placeholder {
    color: $ds-gray-dark;
  }
`;

const SearchContainer = styled.div`
  width: 100%;
  position: relative;
  height: ${SEARCH_INPUT_HEIGHT}px;
  margin-right: 12px;

  @media (max-width: 767px) {
    margin-right: 0;
  }
`;

const SearchIcon = styled.img`
  filter: brightness(0) saturate(100%) invert(56%) sepia(0%) saturate(49%)
    hue-rotate(173deg) brightness(105%) contrast(115%); /* #999 */
  font-weight: 700;
  position: absolute;
  left: 12px;
  top: 13px;
  width: 20px;
  z-index: 1;
`;

const SearchUpsellWrapper = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  bottom: 6px;
  padding: 4px;
  z-index: 1;
  letter-spacing: 0.5px;
  font-size: 10px;
  line-height: 2;
  border-radius: 3px;
  background-color: transparent;
  transition: all 0.25s ease;

  > span {
    color: #0954d3;
  }

  &:hover {
    background-color: #0954d3;

    > span {
      color: #fff;
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const abortRequestIfOngoing = (abortController: RefObject<AbortController>) => {
  if (abortController.current != null) {
    abortController.current.abort();
    // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
    abortController.current = null;
  }
};

export const MIN_AUTOCOMPLETE_LENGTH = 3;
const REQUEST_THROTTLE_MS = 1000;
const UPSELL_TIMEOUT_MS = 300;

type Props = {
  // initial value of searchTerm, for the search page
  initialQuery?: string; // defaults to '' when undefined or null
  focusForced?: boolean;
  isPremium?: boolean;
};

const SearchBar = ({ initialQuery, isPremium = false, ...props }: Props) => {
  const [searchTerm, setSearchTerm] = useState(initialQuery ?? '');
  const [autoCompleteResults, setAutoCompleteResults] = useState<
    SearchResult[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [textareaHeight, setTextareaHeight] = useState(SEARCH_INPUT_HEIGHT);

  const containerEl = useRef<HTMLDivElement>(null);
  const inputEl = useRef<HTMLTextAreaElement>(null);
  const prevAbortController = useRef<AbortController>(null);
  const upsellTimeout = useRef();

  useOnClickOutside(containerEl, () => setFocused(false));

  const getMaxTextareaHeight = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return SEARCH_TEXTAREA_MAX_HEIGHT_MOBILE;
    }

    return SEARCH_TEXTAREA_MAX_HEIGHT;
  }, []);

  const resizeTextarea = useCallback(() => {
    const el = inputEl.current;
    if (!el) return;
    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const maxHeight = getMaxTextareaHeight();
    const newHeight = Math.max(
      SEARCH_INPUT_HEIGHT,
      Math.min(scrollH, maxHeight),
    );
    el.style.height = `${newHeight}px`;
    el.style.overflowY = scrollH > maxHeight ? 'auto' : 'hidden';
    setTextareaHeight(newHeight);
  }, [getMaxTextareaHeight]);

  const collapseTextarea = useCallback(() => {
    const el = inputEl.current;
    if (!el) return;
    el.style.height = `${SEARCH_INPUT_HEIGHT}px`;
    el.style.overflowY = 'hidden';
    setTextareaHeight(SEARCH_INPUT_HEIGHT);
  }, []);

  const searchParams: { q: string } & Record<string, string> = {
    q: searchTerm,
  };

  const viewAllResults = () => {
    const encodedParams = Object.entries(searchParams).reduce(
      (result, [key, value]) => {
        result[key] = encodeURIComponent(value);
        return result;
      },
      {} as Record<string, string>,
    );
    window.location.href = routes.search_path(encodedParams);
  };

  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAutoCompleteResults = useCallback(
    throttle(
      (query: string, abortController: RefObject<AbortController>) => {
        if (query.length >= MIN_AUTOCOMPLETE_LENGTH) {
          setLoading(true);

          // @ts-expect-error ts-migrate(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
          abortController.current = new AbortController();
          const url = routes.ac_search_path({ q: query, limit: 20 });

          fetch(url, { signal: abortController.current.signal })
            .then((res) => {
              if (!res.ok) {
                throw Error(res.statusText);
              }
              return res.json();
            })
            .then((data) =>
              setAutoCompleteResults(
                // 'Query' type is always rendered without need for api
                // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'result' implicitly has an 'any' type.
                data.results?.filter((result) => result.type !== 'Query'),
              ),
            )
            .catch(() => {})
            .finally(() => setLoading(false));
        } else {
          setAutoCompleteResults([]);
        }
      },
      REQUEST_THROTTLE_MS,
      { leading: true, trailing: true },
    ),
    [setLoading, setAutoCompleteResults],
  );

  useEffect(() => {
    // If the searchTerm changes, abort any ongoing requests
    abortRequestIfOngoing(prevAbortController);

    fetchAutoCompleteResults(searchTerm, prevAbortController);

    // Abort the request on unmount
    return () => {
      abortRequestIfOngoing(prevAbortController);
    };
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [autoCompleteResults]);

  useEffect(() => {
    if (props.focusForced) inputEl.current?.focus();
  }, [props.focusForced]);

  useEffect(() => {
    if (focused) {
      resizeTextarea();
    }
  }, [searchTerm, focused, resizeTextarea]);

  useEffect(() => {
    if (!focused) {
      collapseTextarea();
    }
  }, [focused, collapseTextarea]);

  // Total items: 1 (Query row) + autocomplete results
  const totalItems = 1 + autoCompleteResults.length;

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      // Allow IME composition (e.g. Chinese, Japanese, Korean) to
      // confirm character selection without triggering search navigation.
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      if (highlightedIndex > 0 && autoCompleteResults[highlightedIndex - 1]) {
        const result = autoCompleteResults[highlightedIndex - 1];
        window.location.href = result.entity_url;
      } else if (searchTerm.length > 0) {
        viewAllResults();
      }
    } else if (e.key === 'Escape') {
      setHighlightedIndex(-1);
      setFocused(false);
    }
  };

  return (
    /* id for SearchPopup portal to attach */
    <SearchContainer
      id="nav--search-input"
      ref={containerEl}
      style={
        {
          '--search-overlay-top': `${textareaHeight + SEARCH_OVERLAY_GAP}px`,
        } as React.CSSProperties
      }
    >
      <SearchTextarea
        ref={inputEl}
        value={searchTerm}
        placeholder="Search"
        rows={1}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
          setSearchTerm(event.target.value.replace(/\n/g, ' '));
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
      />
      <SearchIcon
        src={$image_url('icons/fa/search.svg')}
        style={{ width: 14 }}
      />
      {isMounted && (
        <AutoCompleteResults
          show={focused}
          searchParams={searchParams}
          results={autoCompleteResults}
          loading={loading}
          highlightedIndex={highlightedIndex}
          topOffset={textareaHeight + SEARCH_OVERLAY_GAP}
        />
      )}

      <SearchUpsellWrapper
        onMouseEnter={() => {
          clearTimeout(upsellTimeout.current);
          setUpsellOpen(true);
        }}
        onMouseLeave={() =>
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
          (upsellTimeout.current = setTimeout(
            () => setUpsellOpen(false),
            UPSELL_TIMEOUT_MS,
          ))
        }
      >
        <Icon name="keyboard_arrow_down" />
        {isMounted && (
          <DropdownContainer>
            <SearchUpsell show={upsellOpen} isPremium={isPremium} />
          </DropdownContainer>
        )}
      </SearchUpsellWrapper>
    </SearchContainer>
  );
};

export default SearchBar;
